import { Trans, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { t } from "i18next";
import moment from "moment";
import { Icon, Loader, Menu } from "semantic-ui-react";

import SearchParams from "simplydo/core/searchParams";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";

import api from "api";
import util from "utils/utils";

import AnnouncementReport from "./AnnouncementReport";
import AddedToCalendarEvent from "./Challenges/AddedToCalendarEvent";
import AiChallengeFormatContent from "./Challenges/AiChallengeFormat";
import AssessorAssignedContent from "./Challenges/AssessorAssigned";
import ChallengeClosed from "./Challenges/ChallengeClosed";
import ChallengeTranslateContent from "./Challenges/ChallengeTranslate";
import ClosingSoonContent from "./Challenges/ClosingSoon";
import RequestOpen from "./Challenges/RequestOpen";
import StageChangedContent from "./Challenges/StageChanged";
import DiscussionApproval from "./Group/DiscussionApproval";
import DiscussionPostedContent from "./Group/DiscussionPosted";
import DiscussionReacted from "./Group/DiscussionReacted";
import DiscussionReplied from "./Group/DiscussionReplied";
import GroupInvitedContent from "./Group/Invited";
import InvitedActions from "./Group/InvitedActions";
import GroupJoinRequestedContent from "./Group/JoinRequested";
import JoinRequestedActions from "./Group/JoinRequestedActions";
import Joined from "./Group/Joined";
import AddedAsCollaboratorContent from "./Idea/CollaboratorAdded";
import CommentReacted from "./Idea/CommentReacted";
import IdeaCommentReply from "./Idea/CommentReply";
import IdeaCommentedContent from "./Idea/Commented";
import DeadlineApproaching from "./Idea/DeadlineApproaching";
import DeadlineSet from "./Idea/DeadlineSet";
import IdeaSendEmail from "./Idea/IdeaSendEmail";
import IdeaSubmittedContent from "./Idea/Submitted";
import IdeaVotedContent from "./Idea/Voted";
import {
  IdeaBusinessProfileInvited,
  IdeaBusinessProfileInvitedActions,
} from "./IdeaBusinessProfileInvited";
import MentionedContent from "./Mentioned";
import PollClosed from "./PollClosed";
import ProjectAssignedAssignee from "./Project/ProjectAssignedAssignee";
import ProjectAssignedManager from "./Project/ProjectAssignedManager";
import ProjectIdeaCommented from "./Project/ProjectIdeaCommented";
import ProjectIdeaMoved from "./Project/ProjectIdeaMoved";
import ProjectIdeaReplied from "./Project/ProjectIdeaReplied";
import UnlockedAchievementContent from "./UnlockedAchievement";

const mentions = [
  {
    type: "mentionedDiscussionComment",
    message: "mentioned you in a discussion",
    link: "discussions",
    location: "",
    id: "discussion",
  },
  {
    type: "mentionedIdeaComment",
    message: `mentioned you in ${t("generic.ideaWithArticle")} comment`,
    link: "ideas",
    location: "",
    id: "idea",
  },
  {
    type: "mentionedIdeaProjectComment",
    message: "mentioned you in a project board comment",
    link: "challenges",
    location: "board",
  },
];

const notificationAllowlist = [
  "ideaCommented",
  "ideaSubmitted",
  "ideaVoted",
  "ideaSendEmail",
  "collaboratorAdded",
  "mentionedDiscussionComment",
  "mentionedIdeaComment",
  "mentionedIdeaProjectComment",
  "closingSoon",
  "stageChanged",
  "challengeAddedToCalendarEvent",
  "challengeClosed",
  "requestOpenChallenge",
  "discussionPosted",
  "reactedDiscussionComment",
  "discussionCommentReply",
  "discussionPostRequiresApproval",
  "unlockedAchievement",
  "projectIdeaCommented",
  "projectIdeaReplied",
  "projectIdeaMoved",
  "projectAssignedAssignee",
  "projectAssignedManager",
  "assessorAssigned",
  "groupJoinRequested",
  "groupInvited",
  "groupUserJoined",
  "ideaCommentReply",
  "reactedIdeaComment",
  "pollClosed",
  "announcementReport",
  "ideaBusinessProfileInvited",
  "ideaDeadlineApproaching",
  "ideaDeadlineSet",
  "announcement",
  "challengeTranslate",
  "aiChallengeFormat",
];

const actionableNotifications = [
  "groupJoinRequested",
  "groupInvited",
  "ideaBusinessProfileInvited",
];

export const NotificationLocation = (notification) => {
  switch (notification.notificationType) {
    case "ideaBusinessProfileInvited": {
      const query = new SearchParams({
        businessProfileId: notification.ideaBusinessProfile,
      });
      return `${window.location.pathname}${query.toSafeString()}`;
    }
    case "ideaCommented":
    case "ideaCommentReply":
    case "reactedIdeaComment": {
      const query = new SearchParams({
        comment: notification.events?.[0]?.commentId,
      });
      return `/ideas/${notification.idea}${query.toSafeString()}`;
    }
    case "collaboratorAdded":
    case "ideaVoted":
      return `/ideas/${notification.idea || notification.events[0].idea}`;
    case "stageChanged":
    case "closingSoon":
    case "challengeClosed":
    case "requestOpenChallenge":
      return `/challenges/${notification.challenge}`;
    case "challengeAddedToCalendarEvent":
      return `/challenges/${notification.challenge}/planner`;
    case "assessorAssigned":
      return `/challenges/${notification.challenge}/assessments`;
    case "ideaSubmitted":
    case "ideaDeadlineSet":
    case "ideaDeadlineApproaching": {
      const { events = [] } = notification;
      if (events.length > 1) {
        return `/challenges/${events[0].ownerIdea.challenge}/ideas`;
      }
      return `/ideas/${events[0].idea}`;
    }
    case "reactedDiscussionComment":
    case "discussionCommentReply":
    case "discussionPostRequiresApproval":
    case "discussionPosted": {
      const query = new SearchParams({
        comment: notification.events?.[0]?.commentId,
      });
      return `/discussions/${notification.discussion}${query.toSafeString()}`;
    }
    case "mentionedDiscussionComment":
    case "mentionedIdeaComment": {
      const mentionedInfo = mentions.find(
        (m) => m.type === notification.notificationType,
      );
      const query = new SearchParams({
        comment: notification.events?.[0]?.commentId,
      });
      return `/${mentionedInfo.link}/${notification[mentionedInfo.id]}/${mentionedInfo.location}${query.toSafeString()}`;
    }
    case "mentionedIdeaProjectComment": {
      const query = new SearchParams({
        idea: notification.events?.[0]?.idea,
        comment: notification.events?.[0]?.commentId,
      });
      return `/challenges/${notification.challenge}/board${query.toSafeString()}`;
    }
    case "unlockedAchievement":
      return "/achievements";
    case "projectIdeaCommented":
    case "projectIdeaReplied": {
      const query = new SearchParams({
        idea: notification.idea,
        comment: notification.events?.[0]?.commentId,
      });
      return `/challenges/${notification.challenge ?? notification.ownerChallenge?._id}/board${query.toSafeString()}`;
    }
    case "projectIdeaMoved":
    case "projectAssignedAssignee":
    case "projectAssignedManager": {
      const query = new SearchParams({ idea: notification.idea });
      return `/challenges/${notification.challenge ?? notification.ownerChallenge?._id}/board${query.toSafeString()}`;
    }
    case "pollClosed":
      return `/admin/polls/${notification.context.pollId}/results`;
    case "groupJoinRequested":
    case "groupInvited":
      return "/";
    case "groupUserJoined":
      return `/groups/${notification.group}`;
    case "announcementReport":
      return notification.context.logLink;
    case "challengeTranslate":
      return `/challenges/${notification.challenge}`;
    case "aiChallengeFormat":
      return `/challenges/${notification.challenge}/settings/format`;
    default:
      return null;
  }
};

const AvatarImage = (source) => (
  <ImageWithFallback
    style={{
      width: 50,
      height: 50,
      objectFit: "cover",
      borderRadius: 10,
      backgroundColor: "#e9ebee",
    }}
    src={source}
    fallbackSrc={util.avatarUrl()}
  />
);

export const NotificationAvatarImage = (notification, user) => {
  switch (notification.notificationType) {
    case "ideaCommented":
    case "ideaVoted":
    case "collaboratorAdded":
    case "mentionedDiscussionComment":
    case "mentionedIdeaComment":
    case "projectIdeaCommented":
    case "projectIdeaReplied":
    case "projectIdeaMoved":
    case "projectAssignedAssignee":
    case "projectAssignedManager":
    case "mentionedIdeaProjectComment":
    case "reactedIdeaComment":
    case "ideaBusinessProfileInvited":
    case "ideaCommentReply":
    case "groupUserJoined":
      return AvatarImage(
        util.avatarUrl(notification?.events[0]?.ownerUser || {}),
      );
    case "closingSoon":
    case "stageChanged":
    case "challengeClosed":
    case "challengeAddedToCalendarEvent":
    case "requestOpenChallenge":
    case "ideaSubmitted":
    case "challengeTranslate":
    case "aiChallengeFormat":
    case "assessorAssigned":
      return AvatarImage(util.challengeImage(notification.ownerChallenge));
    case "ideaDeadlineApproaching":
    case "ideaDeadlineSet":
      return AvatarImage(
        util.ideaCoverImage(notification.events[0].ownerIdea, "200x200"),
      );
    case "reactedDiscussionComment":
    case "discussionCommentReply":
    case "discussionPostRequiresApproval":
    case "discussionPosted":
    case "groupJoinRequested":
    case "groupInvited":
      return AvatarImage(util.groupCoverImage(notification.ownerGroup));
    case "unlockedAchievement":
      return AvatarImage(util.avatarUrl(user));
    default:
      return null;
  }
};

export const NotificationContent = (notification) => {
  switch (notification.notificationType) {
    case "ideaCommented":
      return (
        <IdeaCommentedContent
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "reactedIdeaComment":
      return (
        <CommentReacted
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "ideaCommentReply":
      return (
        <IdeaCommentReply
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "ideaDeadlineApproaching":
      return (
        <DeadlineApproaching
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "ideaDeadlineSet":
      return (
        <DeadlineSet
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "closingSoon":
      return (
        <ClosingSoonContent ownerChallenge={notification.ownerChallenge} />
      );
    case "stageChanged":
      return (
        <StageChangedContent ownerChallenge={notification.ownerChallenge} />
      );
    case "requestOpenChallenge":
      return (
        <RequestOpen
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "challengeClosed":
      return <ChallengeClosed ownerChallenge={notification.ownerChallenge} />;
    case "discussionPosted":
      return (
        <DiscussionPostedContent
          ownerGroup={notification.ownerGroup}
          events={notification.events}
        />
      );
    case "ideaSubmitted":
      return (
        <IdeaSubmittedContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "ideaVoted":
      return (
        <IdeaVotedContent
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "collaboratorAdded":
      return (
        <AddedAsCollaboratorContent
          events={notification.events}
          ownerIdea={notification.ownerIdea}
        />
      );
    case "mentionedDiscussionComment":
    case "mentionedIdeaComment":
    case "mentionedIdeaProjectComment":
      return (
        <MentionedContent
          notification={notification}
          mentions={mentions}
          events={notification.events}
          notificationType={notification.notificationType}
        />
      );
    case "unlockedAchievement":
      return <UnlockedAchievementContent events={notification.events} />;
    case "projectIdeaCommented":
      return (
        <ProjectIdeaCommented
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectIdeaReplied":
      return (
        <ProjectIdeaReplied
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectIdeaMoved":
      return (
        <ProjectIdeaMoved
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectAssignedAssignee":
      return (
        <ProjectAssignedAssignee
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "projectAssignedManager":
      return (
        <ProjectAssignedManager
          events={notification.events}
          ownerIdea={notification.ownerIdea}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "assessorAssigned":
      return (
        <AssessorAssignedContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "challengeAddedToCalendarEvent":
      return (
        <AddedToCalendarEvent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
        />
      );
    case "groupJoinRequested":
      return (
        <GroupJoinRequestedContent
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "groupInvited":
      return (
        <GroupInvitedContent
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "groupUserJoined":
      return (
        <Joined
          ownerGroup={notification.ownerGroup}
          events={notification.events}
        />
      );
    case "reactedDiscussionComment":
      return (
        <DiscussionReacted
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "discussionCommentReply":
      return (
        <DiscussionReplied
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "discussionPostRequiresApproval":
      return (
        <DiscussionApproval
          events={notification.events}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "pollClosed":
      return <PollClosed context={notification.context} />;
    case "announcementReport":
      return (
        <AnnouncementReport
          context={notification.context}
          ownerChallenge={notification.ownerChallenge}
          ownerGroup={notification.ownerGroup}
        />
      );
    case "ideaBusinessProfileInvited":
      return (
        <IdeaBusinessProfileInvited
          events={notification.events}
          ownerIdeaBusinessProfile={notification.ownerIdeaBusinessProfile}
        />
      );
    case "challengeTranslate":
      return (
        <ChallengeTranslateContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
          status={notification.status}
        />
      );
    case "aiChallengeFormat":
      return (
        <AiChallengeFormatContent
          events={notification.events}
          ownerChallenge={notification.ownerChallenge}
          status={notification.status}
        />
      );

    default:
      return null;
  }
};

export const NotificationActions = ({ notification, ...rest }) => {
  switch (notification.notificationType) {
    case "groupJoinRequested":
      return <JoinRequestedActions events={notification.events} {...rest} />;
    case "groupInvited":
      return <InvitedActions events={notification.events} {...rest} />;
    case "ideaBusinessProfileInvited":
      return (
        <IdeaBusinessProfileInvitedActions
          events={notification.events}
          {...rest}
        />
      );
    default:
      return null;
  }
};

function NotificationItem({
  notification,
  user,
  markNotificationAsInteracted,
  invitations,
  onInvitationHandled,
  setAnnouncementNotification,
  setAnnouncementModalOpen,
}) {
  const { notificationType, ownerGroup, interacted } = notification;

  if (!notificationType) return null;
  if (!(notificationAllowlist.indexOf(notificationType) > -1)) {
    return null;
  }
  if (notificationType === "discussedPosted" && !ownerGroup) {
    return null;
  }

  if (notification.ownerChallenge && notification.ownerChallenge === null)
    notification.ownerChallenge = { name: "Undefined" };
  if (notification.ownerGroup && notification.ownerGroup === null)
    notification.ownerGroup = { name: "Undefined" };
  if (notification.ownerIdea && notification.ownerIdea === null)
    notification.ownerIdea = { name: "Undefined" };

  const menuItemStyle = {
    borderBottom: "1px solid",
    borderColor: "#E9EBEE",
    padding: ".833em 1em",
    borderRadius: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    textOverflow: "ellipsis",
    backgroundColor: interacted ? null : "#e9ebee",
    marginTop: 5,
    marginBottom: 5,
    width: "99%",
    color: "black",
  };

  if (notification.notificationType === "ideaSendEmail") {
    return (
      <Menu.Item
        onClick={() => {
          // Mimic an announcement notification when opening an idea email
          // Functionally they are both just emails so they can be displayed the same way
          setAnnouncementNotification({
            ...notification,
            events: [
              {
                ownerAnnouncement: {
                  subject: notification.context.subject,
                  body: notification.context.body,
                  createdAt: notification.createdAt,
                },
              },
            ],
          });
          setAnnouncementModalOpen(true);
          markNotificationAsInteracted(notification._id);
        }}
        fitted
        link
        as={Link}
        style={menuItemStyle}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 50,
          }}
        >
          {AvatarImage(
            util.avatarUrl(notification?.events[0]?.ownerUser || {}),
          )}
        </div>
        <div style={{ marginLeft: 10, flex: 1 }}>
          <p
            style={{
              textAlign: "left",
              marginBottom: 0,
              lineHeight: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <IdeaSendEmail
              events={notification.events}
              ownerIdea={notification.ownerIdea}
            />
          </p>
          <p style={{ color: "gray", marginTop: 3 }}>
            {moment(notification.createdAt).fromNow()}
          </p>
        </div>
      </Menu.Item>
    );
  }

  if (notification.notificationType === "announcement")
    return (
      <Menu.Item
        onClick={() => {
          setAnnouncementNotification(notification);
          setAnnouncementModalOpen(true);
          markNotificationAsInteracted(notification._id);
        }}
        fitted
        link
        as={Link}
        style={menuItemStyle}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 50,
          }}
        >
          <Icon size="large" name="announcement" />
        </div>
        <div style={{ marginLeft: 10, flex: 1 }}>
          <p
            style={{
              textAlign: "left",
              marginBottom: 0,
              lineHeight: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Trans
              i18nKey="notifications.newAnnouncement"
              values={{
                name: util.getEventNames(
                  notification.events,
                  "ownerAnnouncement",
                ),
                announcementText: util.pluralise(
                  notification?.events?.length,
                  t("generic.announcement").toLowerCase(),
                  t("generic.announcements").toLowerCase(),
                  false,
                ),
              }}
              components={{ b: <b /> }}
            />
          </p>
          <p style={{ color: "gray", marginTop: 3 }}>
            {moment(notification.createdAt).fromNow()}
          </p>
        </div>
      </Menu.Item>
    );

  return (
    <Menu.Item
      fitted
      link
      as={Link}
      to={NotificationLocation(notification)}
      style={menuItemStyle}
      onClick={() => {
        markNotificationAsInteracted(notification._id);
        if (notification.notificationType === "aiChallengeFormat") {
          api.invalidateQueries(api.challenges.get, [notification.challenge]);
        }
      }}
    >
      <div>
        {notification.status === "pending" ?
          <Loader inline active style={{ marginRight: 10, marginLeft: 8 }} />
        : NotificationAvatarImage(notification, user)}
      </div>
      <div style={{ marginLeft: 10, flex: 1 }}>
        <p
          style={{
            textAlign: "left",
            marginBottom: 0,
            lineHeight: "15px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {NotificationContent(notification)}
          {notification.additionalEvents ?
            ` + ${notification.additionalEvents} more`
          : null}
        </p>
        <p style={{ color: "gray", marginTop: 3 }}>
          {moment(notification.createdAt).fromNow()}{" "}
          {notification.status === "failed" ?
            <span style={{ color: "#e7381c", fontSize: 12, marginTop: -2 }}>
              {" "}
              - Task failed
            </span>
          : null}
        </p>
      </div>
      {actionableNotifications.indexOf(notificationType) > -1 ?
        <div>
          <NotificationActions
            invitations={invitations}
            notification={notification}
            onAccept={onInvitationHandled}
            onReject={onInvitationHandled}
          />
        </div>
      : null}
    </Menu.Item>
  );
}

export default withTranslation()(NotificationItem);
